import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

export interface DeliveryTier {
  maxKm: number | string;
  deliveryCharge: number | string;
  serviceFee: number | string;
  minimumCartCheckout: number | string;
  freeDeliveryAmount: number | string | null;
}

interface DeliveryTierBuilderProps {
  tiers: DeliveryTier[];
  onChange: (tiers: DeliveryTier[]) => void;
  readOnly?: boolean;
}

const defaultTier: DeliveryTier = {
  maxKm: '',
  deliveryCharge: '0',
  serviceFee: '19',
  minimumCartCheckout: '50',
  freeDeliveryAmount: '',
};

export const DeliveryTierBuilder: React.FC<DeliveryTierBuilderProps> = ({
  tiers,
  onChange,
  readOnly = false,
}) => {
  const handleFieldChange = (index: number, field: keyof DeliveryTier, value: string) => {
    const updated = [...tiers];
    updated[index] = {
      ...updated[index],
      [field]: field === 'freeDeliveryAmount' && value === '' ? null : value,
    };
    onChange(updated);
  };

  const handleAddTier = () => {
    onChange([...tiers, { ...defaultTier }]);
  };

  const handleDeleteTier = (index: number) => {
    if (tiers.length <= 1) return;
    onChange(tiers.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Distance (km)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Delivery Fee (R)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Service Fee (R)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Min Cart (R)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Free Delivery (R)</TableCell>
              {!readOnly && <TableCell sx={{ width: 48 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {tiers.map((tier, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    type="number"
                    value={tier.maxKm}
                    onChange={(e) => handleFieldChange(index, 'maxKm', e.target.value)}
                    size="small"
                    inputProps={{ min: 0.1, step: 0.1 }}
                    disabled={readOnly}
                    placeholder="e.g. 5"
                    sx={{ width: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={tier.deliveryCharge}
                    onChange={(e) => handleFieldChange(index, 'deliveryCharge', e.target.value)}
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                    disabled={readOnly}
                    sx={{ width: 110 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={tier.serviceFee}
                    onChange={(e) => handleFieldChange(index, 'serviceFee', e.target.value)}
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                    disabled={readOnly}
                    sx={{ width: 110 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={tier.minimumCartCheckout}
                    onChange={(e) => handleFieldChange(index, 'minimumCartCheckout', e.target.value)}
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                    disabled={readOnly}
                    sx={{ width: 110 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={tier.freeDeliveryAmount ?? ''}
                    onChange={(e) => handleFieldChange(index, 'freeDeliveryAmount', e.target.value)}
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                    disabled={readOnly}
                    placeholder="None"
                    sx={{ width: 110 }}
                  />
                </TableCell>
                {!readOnly && (
                  <TableCell>
                    <IconButton
                      onClick={() => handleDeleteTier(index)}
                      disabled={tiers.length <= 1}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!readOnly && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddTier}
          sx={{ mt: 1 }}
          size="small"
        >
          Add Tier
        </Button>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Each tier covers deliveries up to its km distance. Tiers are sorted by distance on save.
        Leave &quot;Free Delivery&quot; empty for no free delivery at that tier.
      </Typography>
    </Box>
  );
};
